import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, ChefHat, Check } from 'lucide-react';
import { useKitchenStore } from '../../../stores/Assets/kitchen';
import { useSiteStore } from '../../../stores/Assets/site';

const THEME_COLOR = '#c59253';

const StationManagement = ({ state, updateState }) => {
    const { sites } = useSiteStore();
    const { kitchens, getKitchensBySite, createSiteFromOnboarding } = useKitchenStore();
    const [newStationName, setNewStationName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');

    const siteId = sites?.[0]?.site_id;

    useEffect(() => {
        if (siteId && state.stations.length === 0) {
            fetchInitialStations();
        }
    }, [siteId, state.stations.length]);

    const fetchInitialStations = async () => {
        const res = await getKitchensBySite(siteId, { page: 1, perPage: 100 });
        const remoteKitchens = res?.kitchens || [];
        updateState({
            stations: remoteKitchens.map(k => ({ id: k.kitchen_id, name: k.name, token: k.token }))
        });
    };

    const handleAddStation = () => {
        if (!newStationName.trim()) return;
        const newStation = {
            id: Date.now(), // Local ID for now
            name: newStationName,
            token: `token_${Math.random().toString(36).substr(2, 9)}`
        };
        updateState({ stations: [...state.stations, newStation] });
        setNewStationName('');
    };

    const handleRemoveStation = (id) => {
        updateState({
            stations: state.stations.filter(s => s.id !== id),
            // Cleanup links if any
            menuLinks: Object.fromEntries(Object.entries(state.menuLinks).filter(([sid]) => sid !== id)),
            spaceLinks: Object.fromEntries(Object.entries(state.spaceLinks).filter(([sid]) => sid !== id))
        });
    };

    const startEditing = (station) => {
        setEditingId(station.id);
        setEditName(station.name);
    };

    const saveEdit = () => {
        updateState({
            stations: state.stations.map(s => s.id === editingId ? { ...s, name: editName } : s)
        });
        setEditingId(null);
    };

    return (
        <div className="container py-5" style={{ maxWidth: '800px' }}>
            <div className="text-center mb-5">
                <div className="p-3 bg-soft-primary rounded-circle d-inline-flex mb-3">
                    <ChefHat size={32} style={{ color: THEME_COLOR }} />
                </div>
                <h2 className="fw-bold">Manage Your Stations</h2>
                <p className="text-muted">Create the kitchen stations or preparation areas for your business.</p>
            </div>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                <div className="card-header bg-white p-4 border-bottom d-flex gap-3">
                    <input
                        type="text"
                        className="form-control form-control-lg border-2 shadow-none"
                        placeholder="Station Name (e.g. Main Kitchen, Bar, Grill...)"
                        value={newStationName}
                        onChange={(e) => setNewStationName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddStation()}
                    />
                    <button
                        onClick={handleAddStation}
                        className="btn btn-lg text-white px-4 d-flex align-items-center gap-2"
                        style={{ backgroundColor: THEME_COLOR }}
                    >
                        <Plus size={20} /> Add
                    </button>
                </div>
                <div className="card-body p-0">
                    {state.stations.length > 0 ? (
                        <div className="list-group list-group-flush">
                            {state.stations.map((station) => (
                                <div key={station.id} className="list-group-item p-4 d-flex align-items-center justify-content-between hover-light">
                                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                                        <div className="p-2 bg-light rounded-3 border">
                                            <ChefHat size={20} className="text-muted" />
                                        </div>
                                        {editingId === station.id ? (
                                            <input
                                                className="form-control fw-bold border-2"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onBlur={saveEdit}
                                                onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                                                autoFocus
                                            />
                                        ) : (
                                            <div>
                                                <h6 className="mb-0 fw-bold">{station.name}</h6>
                                                <span className="text-muted small">ID: {station.id}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex gap-2">
                                        {editingId === station.id ? (
                                            <button onClick={saveEdit} className="btn btn-soft-success btn-icon rounded-circle"><Check size={16} /></button>
                                        ) : (
                                            <button onClick={() => startEditing(station)} className="btn btn-soft-secondary btn-icon rounded-circle"><Edit2 size={16} /></button>
                                        )}
                                        <button onClick={() => handleRemoveStation(station.id)} className="btn btn-soft-danger btn-icon rounded-circle"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-5 opacity-50">
                            <ChefHat size={48} className="mb-3" />
                            <p>No stations created yet.</p>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .bg-soft-primary { background-color: rgba(197, 146, 83, 0.1) !important; }
                .hover-light:hover { background-color: #f8f9fa !important; }
                .btn-icon { width: 36px; height: 36px; padding: 0; display: flex; align-items: center; justify-content: center; }
            `}</style>
        </div>
    );
};

export default StationManagement;
